import { Checkbox, FormWrapper } from '@ardoq/forms';
import { Multiselect, Select, SelectOption } from '@ardoq/select';
import { PrimaryButton } from '@ardoq/button';
import { Maybe } from '@ardoq/common-helpers';
import { Island, ISLAND_DEFAULT_MAX_WIDTH } from '@ardoq/page-layout';
import { FlexBox } from '@ardoq/layout';
import { ReactNode } from 'react';
import { type I18nLocale, t } from '@ardoq/i18n';

type ComponentSelectionParams = {
  i18nLocale: I18nLocale;
  integrationName: string;
  dataSelectionIsValid: boolean;
  accounts: { label: string; value: string }[];
  componentTypeOptions: SelectOption<string>[];
  selectedComponentTypes: string[];
  accountId: string | null;
  applyFilters: boolean;
  onNext: () => void;
  onConnChange: (accountId: Maybe<string>) => void;
  onComponentTypesChange: (componentTypes: Maybe<string[]>) => void;
  onApplyFilterChange: (applyFilters: boolean) => void;
  children: [React.ReactElement, ...ReactNode[]];
  title?: string;
  subtitle?: string;
  nextButtonLabel?: string;
  connectionLabel?: string;
  connectionPlaceholder?: string;
  componentTypesLabel?: string;
  componentTypesPlaceholder?: string;
  componentTypesHelperText?: string;
  applyFilterCheckboxLabel?: string;
};

export function ComponentSelection({
  i18nLocale,
  integrationName,
  dataSelectionIsValid,
  accounts,
  componentTypeOptions,
  selectedComponentTypes,
  accountId,
  applyFilters,
  onNext,
  onConnChange,
  onComponentTypesChange,
  onApplyFilterChange,
  children: [workspaceSelector, ...children],
  title = t(
    i18nLocale,
    'integrations:componentSelection.title',
    'Select components to export'
  ),
  subtitle = t(
    i18nLocale,
    'integrations:componentSelection.subtitle',
    { integrationName },
    'Specify the {{integrationName}} connection, Ardoq workspace, and component types you want to export.'
  ),
  nextButtonLabel = t(
    i18nLocale,
    'integrations:componentSelection.nextButtonLabel',
    'Next: Map fields'
  ),
  connectionLabel = t(
    i18nLocale,
    'integrations:componentSelection.connectionLabel',
    { integrationName },
    '{{integrationName}} connection'
  ),
  connectionPlaceholder = t(
    i18nLocale,
    'integrations:componentSelection.connectionPlaceholder',
    'Type and select an existing connection'
  ),
  componentTypesLabel = t(
    i18nLocale,
    'integrations:componentSelection.componentTypesLabel',
    'Component types'
  ),
  componentTypesPlaceholder = t(
    i18nLocale,
    'integrations:componentSelection.componentTypesPlaceholder',
    'Type and select component types in Ardoq'
  ),
  componentTypesHelperText = t(
    i18nLocale,
    'integrations:componentSelection.componentTypesHelperText',
    'Choose the component types to sync.'
  ),
  applyFilterCheckboxLabel = t(
    i18nLocale,
    'integrations:componentSelection.applyFilterCheckboxLabel',
    'Apply filter to specify components to sync'
  ),
}: ComponentSelectionParams) {
  return (
    <>
      <FlexBox
        flexDirection="column"
        padding="xlarge"
        align="center"
        width="full"
        style={{ overflow: 'auto' }}
      >
        <FlexBox
          maxWidth={ISLAND_DEFAULT_MAX_WIDTH}
          width="full"
          flexDirection="column"
        >
          <Island
            title={title}
            subtitle={subtitle}
            subtitleWidth="medium"
            minWidth="100%"
            footerContent={
              <FlexBox justify="end" flex={1}>
                <PrimaryButton
                  isDisabled={!dataSelectionIsValid}
                  onClick={onNext}
                >
                  {nextButtonLabel}
                </PrimaryButton>
              </FlexBox>
            }
          >
            <FormWrapper>
              <Select
                value={accountId}
                label={connectionLabel}
                placeholder={connectionPlaceholder}
                options={accounts}
                onValueChange={onConnChange}
              />
              {workspaceSelector}
              <Multiselect
                label={componentTypesLabel}
                placeholder={componentTypesPlaceholder}
                isClearable
                value={selectedComponentTypes}
                helperText={componentTypesHelperText}
                options={componentTypeOptions}
                onValueChange={onComponentTypesChange}
              />
              <Checkbox isChecked={applyFilters} onChange={onApplyFilterChange}>
                {applyFilterCheckboxLabel}
              </Checkbox>
            </FormWrapper>
            {applyFilters && children}
          </Island>
        </FlexBox>
      </FlexBox>
    </>
  );
}
